import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, ReportActionCreators, ICprScoreAuditResponse } from '@nkr/store/index';
import { ListGroupItem } from 'react-bootstrap';
import Grid from './grid';
import Header from './header';
import { Help } from './help';
import { useScoreAudit } from './use-score-audit';

interface IProps {
    centerId: number,
    isAdmin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getFcrAuditScores: (centerId: number, reportDate: string) => Promise<ICprScoreAuditResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const FcrScoreAuditReport = (props: TProps) => {
    const getScoreAudit = (startDate: string, reportDate: string) => props.getFcrAuditScores(props.centerId, reportDate);

    const audit = useScoreAudit({
        centerId: props.centerId,
        reportPath: 'fcr-score-audit-report',
        endDateParam: 'reportDate',
        allowCustomRange: false,
        loadingMessage: 'Getting FCR Scores, one moment please...',
        showLoading: props.showLoading,
        hideLoading: props.hideLoading,
        getScoreAudit
    });

    const { state } = audit;
    const centerAlias = state.center != null ? state.center.alias : '';
    const summaryFavorableCompatible = state.data.filter(x => x.isFavorablePair && x.isCompatiblePair && x.isIncluded).length;
    const fcrScore = Math.floor(audit.summaryIncluded > 0 ? (summaryFavorableCompatible / audit.summaryIncluded) * 100 : 0);

    return (
        <>
            {props.isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 28, fontWeight: 500 }}>{centerAlias} Center FCR Audit</div>
                    <a href={'/report/fcr-report'}>Return to FCR Score Report</a>
                </div>
            )}

            <Header
                reportStart={state.newReportStart}
                reportEnd={state.newReportEnd}
                customRange={false}
                included={state.included}
                favorable={state.favorable}
                compatible={state.compatible}
                transplanted={state.transplanted}
                onReportStartChange={audit.handleReportStartChange}
                onReportEndChange={audit.handleReportEndChange}
                onReportRangeUpdate={audit.handleRefresh}
                onFilterToggle={audit.handleFilterToggle}
            >
                <tr>
                    <td>Favorable Compatible: <a style={{ cursor: 'pointer', fontWeight: 700 }} onClick={() => audit.handlePairTotalClick(true)}>{summaryFavorableCompatible}</a></td>
                    <td># Included: <a style={{ cursor: 'pointer', fontWeight: 700 }} onClick={audit.handleIncludedTotalClick}>{audit.summaryIncluded}</a></td>
                    <td>FCR Result: <strong>{fcrScore.toString()}</strong></td>
                    <td>Transplants in Report Range: <strong>{state.data.length}</strong></td>
                </tr>
            </Header>

            <Help expanded={state.expandHelp} onToggle={audit.handleToggleHelp}>
                <ListGroupItem>
                    <div style={{ fontWeight: 550 }}>Favorable ABO Pair</div>
                    <div style={{ paddingLeft: 20 }}>A non-O (A, B or AB) recipient whose intended donor is blood type O.</div>
                </ListGroupItem>
                <ListGroupItem>
                    <div style={{ fontWeight: 550 }}>Favorable Compatible Ratio (FCR)</div>
                    <div style={{ paddingLeft: 20 }}>Calculated over the 12 months ending on the report date.</div>
                </ListGroupItem>
                <ListGroupItem>
                    <div style={{ fontWeight: 550 }}>Formula for FCR Result</div>
                    <div style={{ paddingLeft: 20, width: 'fit-content' }}>
                        <div style={{ borderBottom: '1px solid black' }}>Favorable ABO, Compatible, Paired & Voucher Transplants</div>
                        <div style={{ textAlign: 'center' }}>Included Transplants</div>
                    </div>
                </ListGroupItem>
            </Help>

            <Grid
                data={audit.data}
                isAdmin={props.isAdmin}
                favorableCount={audit.favorableCount}
                compatibleCount={audit.compatibleCount}
                includedCount={audit.includedCount}
                transplantedCount={audit.transplantedCount}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getFcrAuditScores: (centerId, reportDate) => {
            return dispatch(ReportActionCreators.getFcrScoreAudit(centerId, reportDate));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(FcrScoreAuditReport);
